import { defineStore } from 'pinia';
import { cloneDeep, get, each, find, map } from 'lodash';
import Vue from 'vue';

import CustomerGroupManagementApi from '@/extensions/shop/store/api/customerGroupManagement';
import { useSetupStore } from '@/stores/setup';
import { useSettingsStore } from '@/stores/settings';

export const useShopCustomerGroupStore = defineStore('shopCustomerGroup', {
    state: () => ({
        loading: true,
        customerGroupsPaginated: {
            path: null,
            variables: {
                paginationSizes: [25, 50, 100],
            },
            filters: {},
            pageInfo: {
                total: 0,
                perPage: 25,
                currentPage: 1,
                lastPage: 0,
            },
            allColumns: [],
            activeColumns: [],
            showFilters: false,
            link: {},
            defaultSort: null,
            items: [],
        },
    }),

    actions: {
        addPaginatedCustomerGroupsToState(data) {
            this.customerGroupsPaginated.items = data.items;
            this.customerGroupsPaginated.pageInfo = cloneDeep(data.pageInfo);
            this.customerGroupsPaginated.allColumns = cloneDeep(data.config.allColumns);
            this.customerGroupsPaginated.link = cloneDeep(data.config.link);
            this.customerGroupsPaginated.defaultSort = get(data.config, 'defaultSort') || null;
            this.customerGroupsPaginated.showFilters = get(data.config, 'showFilters') || false;
        },

        defineActiveColumns(activeColumnKeys) {
            const activeColumns = [];
            each(this.customerGroupsPaginated.allColumns, (column) => {
                const allowed = find(activeColumnKeys, (value) => column.key === value);
                if (allowed) activeColumns.push(column);
            });

            if (activeColumns.length) {
                this.customerGroupsPaginated.activeColumns = activeColumns;
            }
        },

        defineItemsPerPage(perPage) {
            this.customerGroupsPaginated.pageInfo.perPage = perPage;
        },

        defineCurrentPage(currentPage) {
            this.customerGroupsPaginated.pageInfo.currentPage = currentPage;
        },

        defineFilterValue({ key, value }) {
            if (
                value.match === '' &&
                value.like === '' &&
                value.from === '' &&
                value.to === '' &&
                value.in.length === 0
            ) {
                Vue.delete(this.customerGroupsPaginated.filters, key);
            } else {
                Vue.set(this.customerGroupsPaginated.filters, key, value);
            }
        },

        defineFilterVisibility(value) {
            Vue.set(this.customerGroupsPaginated, 'showFilters', value);
        },

        defineSorting({ key, value }) {
            Vue.set(this.customerGroupsPaginated.variables, 'order', key);
            Vue.set(this.customerGroupsPaginated.variables, 'direction', value);
        },

        updateLoading(value) {
            this.loading = value;
        },

        fetchCustomerGroupsPaginated() {
            return CustomerGroupManagementApi.fetchCustomerGroupsPaginated(this.customerGroupsPaginated).then((data) => {
                this.addPaginatedCustomerGroupsToState(data);

                const activeColumnKeys = map(data.config.activeColumnKeys, 'key');
                this.defineActiveColumns(activeColumnKeys);
            });
        },

        fetchAll(variables = {}) {
            return CustomerGroupManagementApi.fetchAll(variables);
        },

        async save(group) {
            const saved = await CustomerGroupManagementApi.save(group);
            this.syncToSetupStore(saved);
            return saved;
        },

        async delete(id) {
            const result = await CustomerGroupManagementApi.delete(id);
            this.removeFromSetupStore(id);
            return result;
        },

        // Keep setupStore.activeCms.shop.customerGroups in sync with the
        // saved/deleted group so that surfaces reading from there (Domain
        // partial, GuestMarketRules dropdown) update without a hard reload.
        // Also mirror the backend's exclusivity rule:
        //   - default = true is exclusive per shop (auto-disabled by the mutation)
        //   - guest_eligible has NO per-currency uniqueness constraint — multiple
        //     groups with the same currency are allowed (e.g. DE/EUR + CH/EUR)
        syncToSetupStore(savedGroup) {
            if (!savedGroup || !savedGroup.id) return;
            const setupStore = useSetupStore();
            const existing = get(setupStore.activeCms, 'shop.customerGroups');
            if (!Array.isArray(existing)) return;

            existing.forEach((group, i) => {
                if (group.id === savedGroup.id) return;

                if (savedGroup.default && group.default) {
                    Vue.set(existing, i, { ...group, default: false });
                }
            });

            const idx = existing.findIndex((group) => group.id === savedGroup.id);
            if (idx >= 0) {
                Vue.set(existing, idx, savedGroup);
            } else {
                existing.push(savedGroup);
            }
        },
        removeFromSetupStore(id) {
            const setupStore = useSetupStore();
            const existing = get(setupStore.activeCms, 'shop.customerGroups');
            if (Array.isArray(existing)) {
                const idx = existing.findIndex((group) => group.id === id);
                if (idx >= 0) existing.splice(idx, 1);
            }

            // A deleted group must not stay pinned to a Domain row — clear
            // both the activeCms snapshot (used on next form open) and the
            // currently-open form's settingsStore.form.domains (rendered
            // right now in the Domain partial).
            this.unpinDomainsFromGroups([id]);
        },

        // Clear `guestCustomerGroup` on every Domain row pinning a group whose
        // id is in `groupIds`. Touches BOTH:
        //   - setupStore.activeCms.domains (canonical snapshot)
        //   - settingsStore.form.domains  (the in-memory form the admin is
        //     editing right now — this is where the Domain partial reads from)
        // Without the second store, the select keeps rendering the demoted
        // group's id because the option list filters it out.
        unpinDomainsFromGroups(groupIds) {
            if (!Array.isArray(groupIds) || !groupIds.length) return;

            const clearMatching = (domains) => {
                if (!Array.isArray(domains)) return;
                domains.forEach((domain, i) => {
                    const pinnedId = get(domain, 'guestCustomerGroup.id');
                    if (pinnedId && groupIds.includes(pinnedId)) {
                        Vue.set(domains[i], 'guestCustomerGroup', null);
                    }
                });
            };

            const setupStore = useSetupStore();
            clearMatching(get(setupStore.activeCms, 'domains'));

            const settingsStore = useSettingsStore();
            clearMatching(get(settingsStore, 'form.domains'));
        },
    },
});
